import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Database, Download, RefreshCw, HardDrive, Cloud, ShieldCheck, AlertTriangle, CheckCircle, XCircle, RotateCcw } from 'lucide-react';
import { Layout } from '../components/Layout';
import { PageContainer, PageHeaderRow } from '../components/PageStyles';
import { Card, CardHeader } from '../components/Card';
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '../components/TableStyles';
import { ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter } from '../components/ModalStyles';
import { FormGroup, FormLabel, StyledSelect } from '../components/FormStyles';
import { ErrorState } from '../components';
import { useTheme } from '../context/ThemeContext';
import { databaseBackupService } from '../api';

// ─── Styled Components ──────────────────────────────────────────

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const BackupCard = styled.div`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DbName = styled.span`
  font-weight: 600;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.text};
`;

const HealthBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${({ $health }) =>
    $health === 'healthy' ? '#22c55e18' :
    $health === 'warning' ? '#f59e0b18' :
    $health === 'critical' || $health === 'failed' ? '#ef444418' :
    '#6b728018'};
  color: ${({ $health }) =>
    $health === 'healthy' ? '#22c55e' :
    $health === 'warning' ? '#f59e0b' :
    $health === 'critical' || $health === 'failed' ? '#ef4444' :
    '#6b7280'};
`;

const CardDetail = styled.div`
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SummaryBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  svg { flex-shrink: 0; }
`;

const SegmentedControl = styled.div`
  display: flex;
  gap: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Segment = styled.button`
  padding: 6px 14px;
  border: none;
  background: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.backgroundSecondary};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.textSecondary};
  font-size: 0.82rem;
  font-weight: ${({ $active }) => $active ? 600 : 400};
  cursor: pointer;
  &:not(:last-child) { border-right: 1px solid ${({ theme }) => theme.colors.border}; }
  &:hover { opacity: 0.85; }
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${({ $type }) =>
    $type === 'full' ? '#3b82f618' :
    '#6b728018'};
  color: ${({ $type }) =>
    $type === 'full' ? '#3b82f6' :
    '#6b7280'};
`;

const MonoText = styled.span`
  font-family: 'SF Mono', 'Fira Code', 'Fira Mono', monospace;
  font-size: 0.8rem;
`;

const ActionButton = styled.button`
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.text};
  &:hover { background: ${({ theme }) => theme.colors.background}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const RefreshButton = styled(ActionButton)`
  svg { transition: transform 0.3s; }
  &.spinning svg { transform: rotate(360deg); }
`;

const NoticeBox = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EmptyRow = styled.td`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg} !important;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
`;

const WarningBox = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  background: #ef44440d;
  border: 1px solid #ef444440;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: #ef4444;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ConfirmInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-family: 'SF Mono', 'Fira Code', 'Fira Mono', monospace;
  font-size: 0.85rem;
  &:focus { outline: none; border-color: #ef4444; }
`;

const RestoreButton = styled.button`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: #ef4444;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &:hover { background: #dc2626; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const SuccessNotice = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ $error }) => $error ? '#ef44440d' : '#22c55e0d'};
  border: 1px solid ${({ $error }) => $error ? '#ef444440' : '#22c55e40'};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ $error }) => $error ? '#ef4444' : '#22c55e'};
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 4px;
`;

// ─── Helpers ────────────────────────────────────────────────────

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return `${diffDays}d ago`;
};

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString();
};

const healthIcon = (health) => {
  if (health === 'healthy') return <CheckCircle size={14} />;
  if (health === 'warning') return <AlertTriangle size={14} />;
  if (health === 'critical' || health === 'failed') return <XCircle size={14} />;
  return null;
};

const statusToHealth = (status) => {
  if (!status) return 'unknown';
  const s = status.toLowerCase();
  if (s === 'success' || s === 'completed') return 'healthy';
  if (s === 'warning') return 'warning';
  if (s === 'failed' || s === 'error') return 'critical';
  return 'unknown';
};

const TARGET_DATABASES = ['aQRate_Develop', 'aQRate_Production'];

// ─── Component ──────────────────────────────────────────────────

export const DatabaseBackupPage = () => {
  const theme = useTheme();

  const [fetchError, setFetchError] = useState(null);

  // Status section
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState(null);

  // Files section
  const [files, setFiles] = useState([]);
  const [allDatabases, setAllDatabases] = useState([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [filesError, setFilesError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('full');
  const [dbFilter, setDbFilter] = useState('');
  const [downloading, setDownloading] = useState(null);

  // Restore modal
  const [restoreModal, setRestoreModal] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState('');
  const [restoreConfirm, setRestoreConfirm] = useState('');
  const [restoring, setRestoring] = useState(false);

  // Restore notice
  const [restoreNotice, setRestoreNotice] = useState(null);

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);
    setFetchError(null);
    try {
      const data = await databaseBackupService.getStatus();
      setStatus(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setStatus(null);
      } else {
        setStatusError('Failed to load backup status');
        setFetchError(err?.response?.data?.message || 'Failed to load backup data. Please check your connection and try again.');
      }
    } finally {
      setStatusLoading(false);
    }
  }, []);

  // Fetch database names once (unfiltered) for the filter control
  useEffect(() => {
    const fetchDatabases = async () => {
      try {
        const data = await databaseBackupService.getFiles({});
        const dbs = [...new Set((data || []).map(f => f.database).filter(Boolean))].sort();
        setAllDatabases(dbs);
      } catch {
        // Non-critical
      }
    };
    fetchDatabases();
  }, []);

  const fetchFiles = useCallback(async () => {
    setFilesLoading(true);
    setFilesError(null);
    try {
      const params = {};
      if (typeFilter) params.type = typeFilter;
      if (dbFilter) params.database = dbFilter;
      const data = await databaseBackupService.getFiles(params);
      setFiles(Array.isArray(data) ? data : []);
    } catch (err) {
      setFilesError('Failed to load backup files from S3');
    } finally {
      setFilesLoading(false);
    }
  }, [typeFilter, dbFilter]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleDownload = async (key) => {
    setDownloading(key);
    try {
      const result = await databaseBackupService.getDownloadUrl(key);
      if (result?.presignedUrl) {
        window.open(result.presignedUrl, '_blank');
      }
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(null);
    }
  };

  const openRestoreModal = (file) => {
    setRestoreModal(file);
    setRestoreTarget(file.database || '');
    setRestoreConfirm('');
    setRestoreNotice(null);
  };

  const closeRestoreModal = () => {
    setRestoreModal(null);
    setRestoreTarget('');
    setRestoreConfirm('');
  };

  const handleRestore = async () => {
    if (!restoreModal || !restoreTarget || restoreConfirm !== restoreTarget) return;
    setRestoring(true);
    try {
      const result = await databaseBackupService.restoreDatabase({
        s3Key: restoreModal.key,
        targetDatabase: restoreTarget,
      });
      closeRestoreModal();
      setRestoreNotice({
        success: true,
        message: result?.message || `Restore of ${restoreTarget} triggered successfully.`,
      });
      // Refresh status after a short delay to pick up in_progress
      setTimeout(() => fetchStatus(), 3000);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Failed to trigger restore';
      setRestoreNotice({ success: false, message: msg });
      closeRestoreModal();
    } finally {
      setRestoring(false);
    }
  };

  const uniqueDatabases = allDatabases;
  const canRestore = restoreTarget && restoreConfirm === restoreTarget && TARGET_DATABASES.includes(restoreTarget);

  return (
    <Layout>
      <PageContainer theme={theme}>
        <PageHeaderRow theme={theme}>
          <h1>Database Backups</h1>
          <RefreshButton theme={theme} onClick={() => { fetchStatus(); fetchFiles(); }}>
            <RefreshCw size={14} /> Refresh
          </RefreshButton>
        </PageHeaderRow>

        {/* ─── Restore Notice ─────────────────────────────── */}
        {restoreNotice && (
          <SuccessNotice theme={theme} $error={!restoreNotice.success}>
            {restoreNotice.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
            {restoreNotice.message}
            <ActionButton theme={theme} onClick={() => setRestoreNotice(null)} style={{ marginLeft: 'auto' }}>
              Dismiss
            </ActionButton>
          </SuccessNotice>
        )}

        {fetchError && !statusLoading ? (
          <ErrorState
            theme={theme}
            title="Failed to load backup data"
            message={fetchError}
            onRetry={() => { fetchStatus(); fetchFiles(); }}
          />
        ) : (<>
        {/* ─── Status Section ──────────────────────────────── */}
        <SectionTitle theme={theme}>
          <Database size={18} /> Backup Status
        </SectionTitle>

        {statusLoading ? (
          <NoticeBox theme={theme}>Loading backup status...</NoticeBox>
        ) : statusError ? (
          <NoticeBox theme={theme}>
            <AlertTriangle size={16} /> {statusError}
            <ActionButton theme={theme} onClick={fetchStatus} style={{ marginLeft: 'auto' }}>Retry</ActionButton>
          </NoticeBox>
        ) : !status ? (
          <NoticeBox theme={theme}>
            <AlertTriangle size={16} /> No backup status found — backups may not have run yet
          </NoticeBox>
        ) : (
          <>
            <CardGrid theme={theme}>
              {status.databases?.map((db, i) => (
                <BackupCard key={i} theme={theme}>
                  <CardTop>
                    <DbName theme={theme}>{db.database}</DbName>
                    <HealthBadge $health={statusToHealth(db.status)}>
                      {healthIcon(statusToHealth(db.status))}
                      {db.status || 'Unknown'}
                    </HealthBadge>
                  </CardTop>
                  <CardDetail theme={theme}>
                    <span>Type: <strong>{db.type}</strong></span>
                    <span>•</span>
                    <span>{db.sizeMB ? `${db.sizeMB.toFixed(1)} MB` : '—'}</span>
                  </CardDetail>
                  <CardDetail theme={theme}>
                    Last backup: <span title={formatDate(db.timestamp)}>{formatTimeAgo(db.timestamp)}</span>
                    {db.ageHours > 0 && <span>({db.ageHours.toFixed(1)}h ago)</span>}
                  </CardDetail>
                  {db.fileName && (
                    <CardDetail theme={theme}>
                      <MonoText>{db.fileName}</MonoText>
                    </CardDetail>
                  )}
                  {db.message && (
                    <CardDetail theme={theme} style={{ color: statusToHealth(db.status) === 'critical' ? '#ef4444' : undefined }}>
                      {db.message}
                    </CardDetail>
                  )}
                </BackupCard>
              ))}
            </CardGrid>

            <SummaryBar theme={theme}>
              <SummaryItem theme={theme}>
                <HardDrive size={14} />
                Disk usage: <strong>{status.diskUsageMB || 0} MB</strong>
              </SummaryItem>
              {status.lastS3Sync && (
                <SummaryItem theme={theme}>
                  <Cloud size={14} />
                  S3 sync: <HealthBadge $health={statusToHealth(status.lastS3Sync.status)} style={{ fontSize: '0.72rem' }}>
                    {status.lastS3Sync.status}
                  </HealthBadge>
                  <span title={formatDate(status.lastS3Sync.timestamp)}>{formatTimeAgo(status.lastS3Sync.timestamp)}</span>
                </SummaryItem>
              )}
              {status.lastVerification && (
                <SummaryItem theme={theme}>
                  <ShieldCheck size={14} />
                  Verification: <HealthBadge $health={statusToHealth(status.lastVerification.status)} style={{ fontSize: '0.72rem' }}>
                    {status.lastVerification.status}
                  </HealthBadge>
                  <span title={formatDate(status.lastVerification.timestamp)}>{formatTimeAgo(status.lastVerification.timestamp)}</span>
                </SummaryItem>
              )}
              {status.lastRestore && (
                <SummaryItem theme={theme}>
                  <RotateCcw size={14} />
                  Last restore: <strong>{status.lastRestore.database}</strong>
                  <HealthBadge $health={statusToHealth(status.lastRestore.status)} style={{ fontSize: '0.72rem' }}>
                    {status.lastRestore.status}
                  </HealthBadge>
                  <span title={formatDate(status.lastRestore.timestamp)}>{formatTimeAgo(status.lastRestore.timestamp)}</span>
                  {status.lastRestore.sourceFile && (
                    <MonoText style={{ fontSize: '0.72rem' }}>({status.lastRestore.sourceFile})</MonoText>
                  )}
                </SummaryItem>
              )}
              {status.lastUpdated && (
                <SummaryItem theme={theme}>
                  Updated: <span title={formatDate(status.lastUpdated)}>{formatTimeAgo(status.lastUpdated)}</span>
                </SummaryItem>
              )}
            </SummaryBar>
          </>
        )}

        {/* ─── Files Section ──────────────────────────────── */}
        <SectionTitle theme={theme}>
          <Cloud size={18} /> Backup Files (S3)
        </SectionTitle>

        <FilterRow theme={theme}>
          <SegmentedControl theme={theme}>
            {['', 'full'].map(t => (
              <Segment key={t} $active={typeFilter === t} theme={theme} onClick={() => setTypeFilter(t)}>
                {t === '' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
              </Segment>
            ))}
          </SegmentedControl>

          {uniqueDatabases.length > 1 && (
            <SegmentedControl theme={theme}>
              <Segment $active={dbFilter === ''} theme={theme} onClick={() => setDbFilter('')}>All</Segment>
              {uniqueDatabases.map(db => (
                <Segment key={db} $active={dbFilter === db} theme={theme} onClick={() => setDbFilter(db)}>
                  {db}
                </Segment>
              ))}
            </SegmentedControl>
          )}
        </FilterRow>

        {filesError ? (
          <NoticeBox theme={theme}>
            <AlertTriangle size={16} /> {filesError}
            <ActionButton theme={theme} onClick={fetchFiles} style={{ marginLeft: 'auto' }}>Retry</ActionButton>
          </NoticeBox>
        ) : (
          <Card theme={theme} style={{ padding: 0, overflow: 'hidden' }}>
            <TableContainer>
              <Table theme={theme}>
                <Thead theme={theme}>
                  <tr>
                    <Th theme={theme}>File</Th>
                    <Th theme={theme}>Database</Th>
                    <Th theme={theme}>Type</Th>
                    <Th theme={theme}>Size</Th>
                    <Th theme={theme}>Date</Th>
                    <Th theme={theme} style={{ width: 160 }}></Th>
                  </tr>
                </Thead>
                <Tbody theme={theme}>
                  {filesLoading ? (
                    <Tr theme={theme}><EmptyRow theme={theme} colSpan={6}>Loading files...</EmptyRow></Tr>
                  ) : files.length === 0 ? (
                    <Tr theme={theme}><EmptyRow theme={theme} colSpan={6}>No backup files found</EmptyRow></Tr>
                  ) : (
                    files.map((file, i) => (
                      <Tr key={i} theme={theme}>
                        <Td theme={theme}><MonoText>{file.fileName}</MonoText></Td>
                        <Td theme={theme}>{file.database}</Td>
                        <Td theme={theme}><TypeBadge $type={file.type}>{file.type}</TypeBadge></Td>
                        <Td theme={theme}>{formatBytes(file.sizeBytes)}</Td>
                        <Td theme={theme} title={formatDate(file.lastModified)}>{formatTimeAgo(file.lastModified)}</Td>
                        <Td theme={theme}>
                          <ActionButtonGroup>
                            <ActionButton
                              theme={theme}
                              onClick={() => handleDownload(file.key)}
                              disabled={downloading === file.key}
                            >
                              <Download size={13} />
                              {downloading === file.key ? '...' : 'Download'}
                            </ActionButton>
                            {file.type === 'full' && (
                              <ActionButton
                                theme={theme}
                                onClick={() => openRestoreModal(file)}
                              >
                                <RotateCcw size={13} /> Restore
                              </ActionButton>
                            )}
                          </ActionButtonGroup>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Card>
        )}

        </>)}

        {/* ─── Restore Confirmation Modal ─────────────────── */}
        <ModalOverlay $isOpen={!!restoreModal} onClick={closeRestoreModal}>
          <ModalContent theme={theme} onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <ModalHeader theme={theme}>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '1.1rem' }}>
                <RotateCcw size={18} /> Restore Database
              </h2>
            </ModalHeader>
            <ModalBody theme={theme}>
              {restoreModal && (
                <>
                  <FormGroup theme={theme}>
                    <FormLabel theme={theme}>Source Backup</FormLabel>
                    <div style={{ fontSize: '0.85rem', color: theme.colors.textSecondary }}>
                      <MonoText>{restoreModal.fileName}</MonoText>
                      <span style={{ marginLeft: 8 }}>{formatBytes(restoreModal.sizeBytes)}</span>
                      <span style={{ marginLeft: 8 }}>{formatDate(restoreModal.lastModified)}</span>
                    </div>
                  </FormGroup>

                  <FormGroup theme={theme}>
                    <FormLabel theme={theme}>Target Database</FormLabel>
                    <StyledSelect
                      theme={theme}
                      value={restoreTarget}
                      onChange={e => { setRestoreTarget(e.target.value); setRestoreConfirm(''); }}
                    >
                      <option value="">Select target database...</option>
                      {TARGET_DATABASES.map(db => (
                        <option key={db} value={db}>{db}</option>
                      ))}
                    </StyledSelect>
                  </FormGroup>

                  <WarningBox theme={theme}>
                    This will overwrite ALL data in the target database. This action cannot be undone.
                  </WarningBox>

                  {restoreTarget && (
                    <FormGroup theme={theme}>
                      <FormLabel theme={theme}>
                        Type <MonoText style={{ color: '#ef4444' }}>{restoreTarget}</MonoText> to confirm
                      </FormLabel>
                      <ConfirmInput
                        theme={theme}
                        type="text"
                        value={restoreConfirm}
                        onChange={e => setRestoreConfirm(e.target.value)}
                        placeholder={restoreTarget}
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </FormGroup>
                  )}
                </>
              )}
            </ModalBody>
            <ModalFooter theme={theme}>
              <ActionButton theme={theme} onClick={closeRestoreModal} disabled={restoring}>
                Cancel
              </ActionButton>
              <RestoreButton
                theme={theme}
                onClick={handleRestore}
                disabled={!canRestore || restoring}
              >
                <RotateCcw size={14} />
                {restoring ? 'Triggering...' : 'Restore Database'}
              </RestoreButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </PageContainer>
    </Layout>
  );
};
